import { useState } from "react";
import { MailX, MailCheck, ChevronDown, ChevronUp } from "lucide-react";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import API from "../../lib/api";

export const ClientEmailSwitch = ({ client, onChanged }) => {
  const enabled = !!client.emails_enabled;
  const held = client.emails_held_count || 0;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggle = async (next) => {
    setSaving(true);
    try {
      await API.put(`/clients/${client.id}/emails`, { enabled: next });
      toast.success(next ? "Emails switched on — automatic emails to this client now go out" : "Emails switched off — automatic emails to this client are held");
      onChanged?.();
    } catch { toast.error("Failed to update email switch"); }
    finally { setSaving(false); }
  };

  const loadHeld = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (items) return;
    try {
      const { data } = await API.get(`/clients/${client.id}/emails/held`);
      setItems(data.items || []);
    } catch { setItems([]); }
  };

  const Icon = enabled ? MailCheck : MailX;
  return (
    <div className="border-b border-border" data-testid="client-email-switch-row">
      <div className="flex items-center justify-between p-4 bg-muted/20">
        <div className="flex items-center gap-3">
          <Icon size={20} className={enabled ? "text-emerald-600" : "text-rose-500"} />
          <div>
            <h2 className="text-sm font-semibold font-[Chivo]">Send Emails to this {client.parent_client_id ? "Sub-Client" : "Client"}</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Off by default. Automatic emails (acknowledgements, updates, quotes, invoices, portal digests, resident updates) are held until this is on. Emails a staff member sends by hand still go out.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {held > 0 && (
            <button type="button" onClick={loadHeld} className="flex items-center gap-1 text-[11px] text-amber-700 hover:underline" data-testid="client-emails-held-button">
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 text-[10px]" data-testid="client-emails-held-count">{held} held</Badge>
              {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
          <span className="text-xs text-muted-foreground" data-testid="client-emails-status">{enabled ? "On" : "Off"}</span>
          <Switch checked={enabled} disabled={saving} onCheckedChange={toggle} data-testid="client-emails-toggle" />
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 bg-muted/10" data-testid="client-emails-held-list">
          {items === null ? <p className="text-[11px] text-muted-foreground">Loading…</p> : items.length === 0 ? <p className="text-[11px] text-muted-foreground">Nothing held yet.</p> : (
            <ul className="divide-y divide-border">
              {items.map(i => (
                <li key={i.id} className="py-1.5 flex items-center justify-between gap-3 text-[11px]">
                  <span className="truncate"><span className="font-medium">{i.subject || "(no subject)"}</span> <span className="text-muted-foreground">→ {i.to}</span></span>
                  <span className="text-muted-foreground shrink-0">{i.created_at ? new Date(i.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
